import { GoPackage } from "react-icons/go";
import { TbPackages } from "react-icons/tb";
import { PhotoButton, SizeLine } from "./ProductCells";

// The cells of the Bundles and Packages lists in My Products, in the Products
// list's style, and the values their grid tiles share. Values come from the
// same fields as the old columns; on purpose (2026-10-19): the bundle weight is
// rounded to 2 decimals (it showed like 107.69999999999999), packages show
// their own unit (it was always Cm / Kg, the unit check never matched
// "Imperial"), a missing size says "Not set", and the bundle cell lists the
// first package names.

// Placeholders when there is no photo: one box for a package, several for a
// bundle (the Bundles tab's icon).
export const PackageIcon = () => <GoPackage aria-hidden="true" />;
export const BundleIcon = () => <TbPackages aria-hidden="true" />;

// The bundle's packages (an array, or an object as the old count allowed).
const bundlePackages = (row) => (row?.packages ? Object.values(row.packages) : []);

// The sum of the bundle's package weights, rounded to 2 decimals; "—" when it
// can't be added up.
export function bundleWeight(row) {
  const sum = row?.packages
    ? bundlePackages(row)
        .map((packageItem) => parseFloat(packageItem?.weight))
        .reduce((accumulator, weight) => accumulator + weight, 0)
    : undefined;
  return Number.isFinite(sum) ? String(Math.round(sum * 100) / 100) : "—";
}

// A package's units from its saved unit system ("Imperial" / "Metric").
export function packageUnits(row) {
  if (row?.unit === "Imperial") return { size: "in", weight: "lb" };
  if (row?.unit === "Metric") return { size: "cm", weight: "kg" };
  return { size: undefined, weight: undefined };
}

export const packageCount = (row) => bundlePackages(row).length;

export const BundlePhotoCell = ({ row }) => (
  <PhotoButton name={row?.name || "bundle"} url={row?.file} icon={<BundleIcon />} />
);

export const PackagePhotoCell = ({ row }) => (
  <PhotoButton name={row?.name || "package"} url={row?.file} icon={<PackageIcon />} />
);

// The name (the old Name column); "-" when there is none.
export const NameCell = ({ row }) => (
  <div className="myprod__cell myprod__product">
    <span className="myprod__pri">{row?.name || "-"}</span>
  </div>
);

// How many packages, then the first two names ("Frame · Bench +2").
export function ContentsCell({ row }) {
  const count = packageCount(row);
  const names = bundlePackages(row).map((item) => item?.name).filter(Boolean);
  const shown = names.slice(0, 2).join(" · ");
  const more = count - Math.min(names.length, 2);
  return (
    <div className="myprod__cell myprod__contents">
      <span className="myprod__pri">
        {count} package{count === 1 ? "" : "s"}
      </span>
      {shown && (
        <span className="myprod__sec" title={names.join(" · ")}>
          {shown}
          {more > 0 && ` +${more}`}
        </span>
      )}
    </div>
  );
}

// The bundle's total weight (the old Weight column, rounded).
export const BundleWeightCell = ({ row }) => (
  <div className="myprod__cell myprod__weight">
    <span className="myprod__pri">{bundleWeight(row)}</span>
  </div>
);

// L*W*H (UNIT), then the weight, in the package's unit (the old Dimensions
// and Weight columns).
export function PackageSizeCell({ row }) {
  const units = packageUnits(row);
  return (
    <div className="myprod__cell myprod__size">
      <SizeLine row={row} unit={units.size?.toUpperCase()} kind="package" />
      <span className="myprod__sec">
        {row?.weight ?? "0"}
        {units.weight ? ` ${units.weight}` : ""}
      </span>
    </div>
  );
}
